/**
 * Leads Jev labeled OUTSIDE the client's service area, with the location
 * evidence WhatConverts gave (city / state / zip, the IP when there is one,
 * the campaign), so the team can build geo exclusions by hand (Art, #11).
 *
 * Read-only: nothing here writes to Google Ads or WhatConverts, and no name,
 * phone or email is ever shown. Agency operators only (the endpoint's gate).
 *
 * Mount it on the WhatConverts Leads page next to the lead labels panel:
 *   <OutOfAreaPanel tenantId={tenantId} />
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { downloadOutOfAreaCsv, getOutOfArea } from './outOfAreaService';

const DAYS = 90;

function place(city: string | null, state: string | null): string {
  return [city, state].filter(Boolean).join(', ') || '—';
}

function day(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? '—' : t.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function OutOfAreaPanel({ tenantId, days = DAYS }: { tenantId: string | null; days?: number }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const q = useQuery({
    queryKey: ['wc-out-of-area', tenantId, days],
    queryFn: () => getOutOfArea(tenantId ?? '', days),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  if (!tenantId) {
    return <p className="text-sm text-muted-foreground">Pick a client to see its out-of-area leads.</p>;
  }
  if (q.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        The out-of-area list could not be loaded. Try again in a moment.
      </p>
    );
  }
  if (q.isLoading || !q.data) {
    return <p className="text-sm text-muted-foreground">Loading out-of-area leads…</p>;
  }
  const { leads, total, by_city, by_zip, location_note } = q.data;
  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No leads labeled outside the service area in the last {days} days.
      </p>
    );
  }

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadOutOfAreaCsv(tenantId, days);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="space-y-4" aria-label="Out-of-area leads">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-foreground">Outside the service area</h3>
          <p className="text-sm text-muted-foreground">
            Qualified but not quotable. Use this list to decide which locations or IPs to exclude in
            Google Ads. Nothing is changed for you.
          </p>
          <p className="text-sm text-muted-foreground">{location_note}</p>
        </div>
        <Button variant="outline" size="sm" onClick={download} disabled={downloading}>
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Download CSV
        </Button>
      </header>
      {downloadFailed && (
        <p role="alert" className="text-sm text-destructive">
          The CSV could not be downloaded. Try again.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <h4 className="mb-1 text-sm font-medium text-foreground">By city</h4>
          <ul className="space-y-0.5 text-sm text-muted-foreground">
            {by_city.slice(0, 10).map((c) => (
              <li key={`${c.city}|${c.state}`} className="flex justify-between">
                <span>{place(c.city, c.state)}</span>
                <span className="tabular-nums text-foreground">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="mb-1 text-sm font-medium text-foreground">By zip</h4>
          <ul className="space-y-0.5 text-sm text-muted-foreground">
            {by_zip.slice(0, 10).map((z) => (
              <li key={z.zip} className="flex justify-between">
                <span>{z.zip}</span>
                <span className="tabular-nums text-foreground">{z.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Location (source shown per row)</th>
              <th className="px-3 py-2 font-medium">Zip</th>
              <th className="px-3 py-2 font-medium">IP</th>
              <th className="px-3 py-2 font-medium">Campaign</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.lead_id} className="border-t border-border">
                <td className="px-3 py-2">{day(l.date_created)}</td>
                <td className="px-3 py-2">{l.lead_type ?? '—'}</td>
                <td className="px-3 py-2" title={l.location_source}>
                  {place(l.city, l.state)}
                  <span className="block text-xs text-muted-foreground">{l.location_source}</span>
                </td>
                <td className="px-3 py-2">{l.zip ?? '—'}</td>
                <td className="px-3 py-2">{l.ip_address ?? '—'}</td>
                <td className="px-3 py-2">{l.campaign ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Showing {leads.length} of {total} in the last {days} days.
      </p>
    </section>
  );
}
